import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { telegramService } from "@/services/telegramService";

interface TelegramConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected: () => void;
}

type Step = "phone" | "code" | "password";

export function TelegramConnectDialog({
  open,
  onOpenChange,
  onConnected,
}: TelegramConnectDialogProps) {
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [phoneCodeHash, setPhoneCodeHash] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setStep("phone");
    setPhone("");
    setCode("");
    setPassword("");
    setPhoneCodeHash("");
    setError(null);
    setLoading(false);
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSendCode = async () => {
    setError(null);
    setLoading(true);
    try {
      const result = await telegramService.sendCode(phone.trim());
      setPhoneCodeHash(result.phoneCodeHash);
      setStep("code");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to send code");
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const result = await telegramService.signIn({
        phone: phone.trim(),
        code: code.trim(),
        phoneCodeHash,
      });
      if (result.needsPassword) {
        setStep("password");
        return;
      }
      onConnected();
      handleClose(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  const handlePassword = async () => {
    setError(null);
    setLoading(true);
    try {
      await telegramService.checkPassword(password);
      onConnected();
      handleClose(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "2FA failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect Telegram</DialogTitle>
          <DialogDescription>
            Sign in with your Telegram account using phone verification.
          </DialogDescription>
        </DialogHeader>

        {step === "phone" && (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="telegram-phone">Phone number</Label>
              <Input
                id="telegram-phone"
                placeholder="+1234567890"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>
        )}

        {step === "code" && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Enter the code sent to your Telegram app for {phone}
            </p>
            <div className="space-y-2">
              <Label htmlFor="telegram-code">Verification code</Label>
              <Input
                id="telegram-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
          </div>
        )}

        {step === "password" && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Two-factor authentication is enabled on your account.
            </p>
            <div className="space-y-2">
              <Label htmlFor="telegram-password">Password</Label>
              <Input
                id="telegram-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          {step === "phone" && (
            <Button onClick={() => void handleSendCode()} disabled={loading || !phone.trim()}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send code
            </Button>
          )}
          {step === "code" && (
            <Button onClick={() => void handleSignIn()} disabled={loading || !code.trim()}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Verify
            </Button>
          )}
          {step === "password" && (
            <Button onClick={() => void handlePassword()} disabled={loading || !password}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Sign in
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
